import React, { useState, useEffect, useMemo } from 'react';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import {Modal, IconButton} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import InfoIcon from '@mui/icons-material/Info';
import {FeatureRequest, FeatureRequestStatusList} from "../../../models/support";
import {fetchFeatureRequestsPaginated, deleteFeatureRequest} from "../../../components/business_logic/feature_request_requests";
import PreviewFeatureRequest from "./preview_feature_request";
import NewFeatureRequest from "./new_feature_request";
import DeleteItemDialog from "../templates/delete_item_dialog";
import CircularProgress from "@mui/material/CircularProgress";
import {useSelector} from "react-redux";
import {RootState} from "../../../store/store";

type SortOption = 'newest' | 'oldest' | 'status' | 'title';

export default function FeatureRequestCatalog() {
    const currentUser = useSelector((state: RootState) => state.session.user);
    const [featureRequests, setFeatureRequests] = useState<FeatureRequest[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
    const [requestToDelete, setRequestToDelete] = useState<FeatureRequest | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [newRequestOpen, setNewRequestOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [sortOption, setSortOption] = useState<SortOption>('newest');
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(true);
    const pageSize = 100;

    useEffect(() => {
        loadRequests().then();
    }, [page]);

    const loadRequests = async () => {
        setLoading(true);
        try {
            const requests = await fetchFeatureRequestsPaginated(page);
            setFeatureRequests(requests);
            // If we got fewer items than pageSize, we're on the last page
            setHasMorePages(requests.length >= pageSize);
        } catch (error) {
            console.error('Failed to load feature requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleRequestClick = (request: FeatureRequest) => {
        setSelectedRequest(request);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedRequest(null);
    };

    const handleOpenNewRequest = () => {
        setNewRequestOpen(true);
    };

    const handleCloseNewRequest = () => {
        setNewRequestOpen(false);
    };

    const handleRequestCreated = () => {
        loadRequests().then();
    };

    const handleDeleteClick = (e: React.MouseEvent, request: FeatureRequest) => {
        e.stopPropagation(); // Prevent card click
        setRequestToDelete(request);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatStatus = (status?: string) => {
        if (!status) return 'N/A';
        const statusOption = FeatureRequestStatusList.find(s => s.value === status);
        return statusOption ? statusOption.label : status.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const getStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (status) {
            case 'open':
                return 'info';
            case 'under_review':
                return 'warning';
            case 'closed':
                return 'default';
            default:
                return 'default';
        }
    };


    // Check if current user matches the request user
    const isCurrentUserRequest = (request: FeatureRequest): boolean => {
        if (!currentUser || !currentUser.id) return false;
        if (!request.user || !request.user.id) return false;
        return currentUser.id === request.user.id;
    };

    // Determine if user info should be shown
    const shouldShowUser = (request: FeatureRequest): boolean => {
        if (request.anonymous) {
            // Only show user if it's the current user's request
            return isCurrentUserRequest(request);
        }
        return true;
    };

    // Get display user info
    const getUserDisplay = (request: FeatureRequest): string => {
        if (request.anonymous && !isCurrentUserRequest(request)) {
            return 'Anonymous';
        }
        if (request.user) {
            return request.user.first_name + " " +  request.user.last_name || request.user.email || 'Unknown User';
        }
        return 'Unknown User';
    };

    // Filter and sort requests
    const filteredAndSortedRequests = useMemo(() => {
        let filtered = featureRequests.filter(request => {
            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesTitle = request.title?.toLowerCase().includes(query);
                const matchesDescription = request.description?.toLowerCase().includes(query);
                if (!matchesTitle && !matchesDescription) return false;
            }

            // Status filter
            return !(statusFilter && request.request_status !== statusFilter);


        });

        // Sort
        filtered.sort((a, b) => {
            switch (sortOption) {
                case 'newest':
                    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dateB - dateA;
                case 'oldest':
                    const dateAOld = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const dateBOld = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dateAOld - dateBOld;
                case 'status':
                    return (a.request_status || '').localeCompare(b.request_status || '');
                case 'title':
                    return (a.title || '').localeCompare(b.title || '');
                default:
                    return 0;
            }
        });

        return filtered;
    }, [featureRequests, searchQuery, statusFilter, sortOption]);

    if (loading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading feature requests...</Typography>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Feature Requests
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNewRequest}
                        size="large"
                    >
                        New Feature Request
                    </Button>
                </Box>

                {/* Information Section */}
                <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'action.hover' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    Share Your Ideas
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Have an idea for a new feature? Share it with us! Browse existing feature requests 
                                    from the community or submit your own. Your feedback helps us improve PennyPusher.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Search, Filter, and Sort Controls */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 3,
                    flexWrap: 'wrap',
                    alignItems: 'center'
                }}>
                    <TextField
                        placeholder="Search feature requests..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 200 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            {FeatureRequestStatusList.map((status) => (
                                <MenuItem key={status.value} value={status.value}>
                                    {status.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortOption}
                            label="Sort By"
                            onChange={(e) => setSortOption(e.target.value as SortOption)}
                        >
                            <MenuItem value="newest">Newest First</MenuItem>
                            <MenuItem value="oldest">Oldest First</MenuItem>
                            <MenuItem value="status">Status</MenuItem>
                            <MenuItem value="title">Title (A-Z)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Requests List */}
            {filteredAndSortedRequests.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <LightbulbIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {featureRequests.length === 0 
                            ? 'No Feature Requests Yet' 
                            : 'No Feature Requests Match Your Filters'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {featureRequests.length === 0
                            ? 'Be the first to share an idea! Click the button above to create a feature request.'
                            : 'Try adjusting your search or filter criteria.'}
                    </Typography>
                    {featureRequests.length === 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleOpenNewRequest}
                        >
                            Create Feature Request
                        </Button>
                    )}
                </Box>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        Feature Requests ({filteredAndSortedRequests.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {filteredAndSortedRequests.map((request) => (
                            <Card
                                key={request.id}
                                variant="outlined"
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                    },
                                    borderColor: 'divider',
                                }}
                                onClick={() => handleRequestClick(request)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1, mr: 2 }}>
                                            <Typography 
                                                variant="h6" 
                                                component="h2" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    mb: 1
                                                }}
                                            >
                                                {request.title || 'Untitled Feature Request'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                                                <Chip 
                                                    label={formatStatus(request.request_status)} 
                                                    size="small" 
                                                    color={getStatusColor(request.request_status)}
                                                />
                                                {request.anonymous && !isCurrentUserRequest(request) && (
                                                    <Chip 
                                                        label="Anonymous" 
                                                        size="small" 
                                                        variant="outlined"
                                                    />
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(request.created_at)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {isCurrentUserRequest(request) && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleDeleteClick(e, request)}
                                                sx={{
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        backgroundColor: 'error.light',
                                                        color: 'error.dark',
                                                    },
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        pt: 2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {shouldShowUser(request) ? (
                                                <>By: <strong>{getUserDisplay(request)}</strong></>
                                            ) : (
                                                <>By: <strong>Anonymous</strong></>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="primary">
                                            View details →
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </>
            )}

            {filteredAndSortedRequests.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={hasMorePages ? page + 1 : page}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                    />
                </Box>
            )}

            {/* Preview Modal */}
            <Modal 
                open={previewOpen && !!selectedRequest}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClosePreview();
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Box 
                    sx={{
                        position: 'relative',
                        width: "90%",
                        maxWidth: "900px",
                        maxHeight: "90vh",
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedRequest && <PreviewFeatureRequest request={selectedRequest} isAdmin={false} />}
                </Box>
            </Modal>

            {/* New Request Modal */}
            <NewFeatureRequest 
                open={newRequestOpen}
                onClose={handleCloseNewRequest}
                onSuccess={handleRequestCreated}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteItemDialog
                itemToDelete={requestToDelete}
                setItemToDelete={setRequestToDelete}
                onDelete={() => deleteFeatureRequest(requestToDelete!)}
                onSuccess={() => {
                    loadRequests().then();
                }}
                itemName={(request) => request.title || 'Untitled Feature Request'}
                itemDetails={(request) => (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            ID: {request.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Status: {formatStatus(request.request_status)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(request.created_at)}
                        </Typography>
                    </>
                )}
                title="Delete Feature Request"
                description="Are you sure you want to delete this feature request?"
                warningMessage="⚠️ This action cannot be undone and will permanently delete this feature request."
                requireNameVerification={false}
            />
        </Container>
    );
}
